<script lang="ts">
	// Stores
	import { devicesFilters } from 'src/stores/ui/filters';
	import { edit, bulkActionEdit } from 'src/stores/ui/edit_lists';
	// Design system components
	import {
		BreadcrumbsFlexWrapper,
		Toolbar,
		ToolbarFilterContainer,
		Tooltip,
	} from '@Shelly/shelly-design-system';
	// Components
	import AddEditButtonsNew from '../../AddEditButtonsNew.svelte';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';
	import EditRoomWizard from './EditRoomWizard/EditRoomWizard.svelte';

	// Group this imports
	import { querystring, replace, location } from 'svelte-spa-router';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	import DeleteRoomModal from './DeleteRoomModal/DeleteRoomModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import DeviceListToggleLayout from '../Devices/DeviceListToggleLayout.svelte';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import { getSharedHomeManagedTooltip } from 'src/helpers/houses/shared_home_messaging_helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';

	export let id: string;
	export let type;
	export let list;
	export let currentRoute;
	export let showBulkActions: boolean = false;
	export let itemsLength: number = 0;
	export let showDeviceListToggle: boolean = false; // Device list/grid toggle only applies to the room devices tab

	$: emptyList = itemsLength === 0;
	$: isSharedHomeActive = isActiveHomeShared($homes.list);

	let showEditRoomModal = false;
	let showDeleteRoomModal = false;
	let showSharedManagedModal = false;

	// here we filter devices by route from energy tab -> energy by appliance type
	const routeFilter = Number($querystring.split('=')[1]);

	if (routeFilter) {
		Object.values($devicesFilters.appliance_types).map((filter) => {
			if (filter.id === routeFilter) {
				filter.selected = true;
			} else {
				filter.selected = false;
			}
			return filter;
		});
		replace(`${$location}`);
	}
</script>

{#key showEditRoomModal}
	<Toolbar
		class="mb-m"
		hideSlotBottom={!$is_tablet || $is_mobile}
		hideSlotRightSide={$is_mobile || (isSubAccount() && !isSharedHomeActive)}
		hideSlotLeftSide={!($is_mobile || (!$is_mobile && !$is_tablet))}
		isSticky
		useNewLayout={$showNewLayout}
	>
		<BreadcrumbsFlexWrapper
			slot="breadcrumbs"
			gap="s"
			hideSlotRight={!$is_mobile ||
				($edit && !$bulkActionEdit) ||
				($edit && $bulkActionEdit && $is_mobile)}
		>
			<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
				<RoomBreadCrumbs {id} />
			</div>

			<div slot="right" class="d-flex align-items-center gap-s">
				{#if !$edit && showDeviceListToggle}
					{#if isSharedHomeActive}
						<Tooltip placement="top" title={getSharedHomeManagedTooltip('change_view')}>
							<DeviceListToggleLayout disabled />
						</Tooltip>
					{:else}
						<DeviceListToggleLayout />
					{/if}
				{/if}

				<AddEditButtonsNew
					{showBulkActions}
					managed={isSharedHomeActive}
					on:managed_click={() => (showSharedManagedModal = true)}
					on:add_click={async () => {
						edit.set(false);
					}}
					on:edit_room_click={() => {
						showEditRoomModal = true;
					}}
					on:delete_room_click={() => {
						showDeleteRoomModal = true;
					}}
					disableEditButton={emptyList}
					disableReorderButton={itemsLength <= 1}
					on:select-all
					on:submit-click
				/>
			</div>
		</BreadcrumbsFlexWrapper>

		<svelte:fragment slot="leftSide">
			{#if !$is_mobile && !$is_tablet}
				<div class="d-flex gap-s max-width-fit-content align-items-center">
					<RoomToolbar />
				</div>
			{/if}

			{#if $is_mobile}
				{#if $edit && $bulkActionEdit}
					<AddEditButtonsNew
						{showBulkActions}
						showFinishReorderBtn
						on:add_click={async () => {
							edit.set(false);
						}}
						on:edit_room_click={() => {
							showEditRoomModal = true;
						}}
						on:delete_room_click={() => {
							showDeleteRoomModal = true;
						}}
						disableEditButton={emptyList}
						disableReorderButton={itemsLength <= 1}
						on:select-all
						on:submit-click
					/>
				{/if}
				<ToolbarFilterContainer>
					<div class="d-flex gap-s max-width-fit-content align-items-center">
						<RoomToolbar />
					</div>
				</ToolbarFilterContainer>
				{#if $edit && !$bulkActionEdit}
					<AddEditButtonsNew
						{showBulkActions}
						showFinishReorderBtn
						on:add_click={async () => {
							edit.set(false);
						}}
						on:edit_room_click={() => {
							showEditRoomModal = true;
						}}
						on:delete_room_click={() => {
							showDeleteRoomModal = true;
						}}
						disableEditButton={emptyList}
						disableReorderButton={itemsLength <= 1}
						on:select-all
						on:submit-click
					/>
				{/if}
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			{#if !$edit && showDeviceListToggle}
				{#if isSharedHomeActive}
					<Tooltip placement="top" title={getSharedHomeManagedTooltip('change_view')}>
						<DeviceListToggleLayout disabled />
					</Tooltip>
				{:else}
					<DeviceListToggleLayout />
				{/if}
			{/if}
			{#if !$is_mobile}
				<AddEditButtonsNew
					{showBulkActions}
					managed={isSharedHomeActive}
					on:managed_click={() => (showSharedManagedModal = true)}
					on:add_click={async () => {
						edit.set(false);
					}}
					on:edit_room_click={() => {
						showEditRoomModal = true;
					}}
					on:delete_room_click={() => {
						showDeleteRoomModal = true;
					}}
					disableEditButton={emptyList}
					disableReorderButton={itemsLength <= 1}
					on:select-all
					on:submit-click
				/>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			{#if $is_tablet && !$is_mobile}
				<ToolbarFilterContainer>
					<div class="d-flex gap-s max-width-fit-content align-items-center">
						<RoomToolbar />
					</div>
				</ToolbarFilterContainer>
			{/if}
		</svelte:fragment>
	</Toolbar>
{/key}

<EditRoomWizard bind:showEditRoomModal {id} {list} {currentRoute} {type} />
<DeleteRoomModal bind:showDeleteRoomModal {id} {list} {currentRoute} {type} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="edit_entity" />
